import { api } from '../utils/api';
import { uploadApi } from '../utils/api';

export interface Document {
  DocumentID: number;
  Title: string;
  FileName: string;
  FilePath: string;
  FileSize: number;
  MimeType: string;
  Status: 'uploaded' | 'processing' | 'processed' | 'error' | 'awaiting_review';
  CreatedAt: string;
  ProcessedAt?: string;
  ErrorMessage?: string;
}

export interface DocumentChunk {
  ChunkID: number;
  DocumentID: number;
  Content: string;
  PageNumber: number;
  ChunkIndex: number;
}

export interface Knowledge {
  KnowledgeID: number;
  Title: string;
  Content: string;
  Category: string;
  DocumentID?: number;
  Status: 'draft' | 'pending_review' | 'published';
  CreatedBy: number;
  CreatedAt: string;
  ApprovedBy?: number;
  ApprovedAt?: string;
  Notes?: string;
  Tags: string[];
}

export interface KnowledgeStatusMetrics {
  draft: number;
  pending_review: number;
  published: number;
}

export interface Tag {
  TagID: number;
  Name: string;
  CreatedAt: string;
}

export interface FAQ {
  FAQID: number;
  Category: string;
  Question: string;
  Answer: string;
  Status: 'draft' | 'published';
  CreatedAt: string;
  UpdatedAt: string;
  UpdatedBy: number;
}

export interface Settings {
  [key: string]: string;
}

export interface PlaygroundRequest {
  question: string;
  documentIds?: number[];
  tags?: string[];
  categories?: string[];
}

export interface PlaygroundResponse {
  success: boolean;
  answer: string;
  confidence: number;
  context?: string;
}

class VirtualAdvisorAdminService {
  // Document Management
  async uploadDocument(file: File, title?: string): Promise<{ success: boolean; documentId?: number; message: string }> {
    const formData = new FormData();
    formData.append('document', file);
    if (title) {
      formData.append('title', title);
    }

    const response = await uploadApi.post('/api/v1/virtual-advisor/admin/documents/upload', formData);

    return response.data;
  }

  async getDocuments(filters?: { status?: string; search?: string }, pagination?: { page: number; size: number }): Promise<{ success: boolean; data: Document[]; total: number }> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.search) params.append('search', filters.search);
    if (pagination?.page) params.append('page', pagination.page.toString());
    if (pagination?.size) params.append('size', pagination.size.toString());

    const response = await api.get(`/api/v1/virtual-advisor/admin/documents?${params}`);
    return response.data;
  }

  async processDocument(documentId: number): Promise<{ success: boolean; chunksCount?: number; message: string }> {
    const response = await api.post(`/api/v1/virtual-advisor/admin/documents/${documentId}/process`);
    return response.data;
  }

  async syncDocumentToFileSearch(documentId: number): Promise<{ success: boolean; googleFileId?: string; message: string }> {
    const response = await api.post(`/api/v1/virtual-advisor/admin/documents/${documentId}/sync-file-search`);
    return response.data;
  }

  async getDocumentChunks(documentId: number): Promise<{ success: boolean; data: DocumentChunk[] }> {
    const response = await api.get(`/api/v1/virtual-advisor/admin/documents/${documentId}/chunks`);
    return response.data;
  }

  async deleteDocument(documentId: number): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/api/v1/virtual-advisor/admin/documents/${documentId}`);
    return response.data;
  }

  // Knowledge Management
  async createKnowledge(knowledge: {
    title: string;
    content: string;
    category: string;
    documentId?: number;
    tags?: string[];
    status?: string;
    notes?: string;
  }): Promise<{ success: boolean; knowledgeId?: number; message: string }> {
    const response = await api.post('/api/v1/virtual-advisor/admin/knowledge', knowledge);
    return response.data;
  }

  async updateKnowledge(knowledgeId: number, knowledge: {
    title: string;
    content: string;
    category: string;
    documentId?: number;
    tags?: string[];
    status?: string;
    notes?: string;
  }): Promise<{ success: boolean; message: string }> {
    const response = await api.put(`/api/v1/virtual-advisor/admin/knowledge/${knowledgeId}`, knowledge);
    return response.data;
  }

  async getKnowledge(filters?: { status?: string; category?: string; search?: string; documentId?: number }, pagination?: { page: number; size: number }): Promise<{ success: boolean; data: Knowledge[]; total: number }> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.category) params.append('category', filters.category);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.documentId) params.append('documentId', filters.documentId.toString());
    if (pagination?.page) params.append('page', pagination.page.toString());
    if (pagination?.size) params.append('size', pagination.size.toString());

    const response = await api.get(`/api/v1/virtual-advisor/admin/knowledge?${params}`);
    return response.data;
  }

  async getKnowledgeStatusMetrics(): Promise<{ success: boolean; data: KnowledgeStatusMetrics; total: number }> {
    const response = await api.get('/api/v1/virtual-advisor/admin/knowledge/status-metrics');
    return response.data;
  }

  async publishKnowledge(knowledgeId: number, notes?: string): Promise<{ success: boolean; message: string }> {
    const response = await api.post(`/api/v1/virtual-advisor/admin/knowledge/${knowledgeId}/publish`, { notes });
    return response.data;
  }

  // Tag Management
  async getTags(): Promise<{ success: boolean; data: Tag[] }> {
    const response = await api.get('/api/v1/virtual-advisor/admin/tags');
    return response.data;
  }

  async createTag(name: string): Promise<{ success: boolean; tagId?: number; message: string }> {
    const response = await api.post('/api/v1/virtual-advisor/admin/tags', { name });
    return response.data;
  }

  async deleteTag(tagId: number): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/api/v1/virtual-advisor/admin/tags/${tagId}`);
    return response.data;
  }

  // FAQ Management
  async getFAQs(filters?: { status?: string; category?: string }, pagination?: { page: number; size: number }): Promise<{ success: boolean; data: FAQ[]; total: number }> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.category) params.append('category', filters.category);
    if (pagination?.page) params.append('page', pagination.page.toString());
    if (pagination?.size) params.append('size', pagination.size.toString());

    const response = await api.get(`/api/v1/virtual-advisor/admin/faqs?${params}`);
    return response.data;
  }

  async createFAQ(faq: {
    category: string;
    question: string;
    answer: string;
    status?: string;
  }): Promise<{ success: boolean; faqId?: number; message: string }> {
    const response = await api.post('/api/v1/virtual-advisor/admin/faqs', faq);
    return response.data;
  }

  async updateFAQ(faqId: number, updates: Partial<{
    category: string;
    question: string;
    answer: string;
    status: string;
  }>): Promise<{ success: boolean; message: string }> {
    const response = await api.put(`/api/v1/virtual-advisor/admin/faqs/${faqId}`, updates);
    return response.data;
  }

  // Settings Management
  async getSettings(): Promise<{ success: boolean; data: Settings }> {
    const response = await api.get('/api/v1/virtual-advisor/admin/settings');
    return response.data;
  }

  async updateSettings(updates: { [key: string]: string }): Promise<{ success: boolean; message: string }> {
    const response = await api.put('/api/v1/virtual-advisor/admin/settings', updates);
    return response.data;
  }

  // Import/Export
  async exportKnowledge(filters?: { status?: string; category?: string }): Promise<{ success: boolean; data: any[] }> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.category) params.append('category', filters.category);

    const response = await api.get(`/api/v1/virtual-advisor/admin/export/knowledge?${params}`);
    return response.data;
  }

  // Playground
  async playgroundAsk(request: PlaygroundRequest): Promise<PlaygroundResponse> {
    const response = await api.post('/api/v1/virtual-advisor/admin/playground/ask', request);
    return response.data;
  }
}

export const virtualAdvisorAdminService = new VirtualAdvisorAdminService();
